import { ToggleGroup, ToggleGroupItem } from './shadcn/toggle-group'
import { Tooltip, TooltipContent, TooltipTrigger } from './shadcn/tooltip'

interface BaseProps<T extends { key: string; label: React.ReactNode }> {
  options: T[]
  onClick: (key: T['key']) => void
  flexDirection?: 'row' | 'column'
}

interface SingleProps<T extends { key: string; label: React.ReactNode }> extends BaseProps<T> {
  type: 'single'
  selectedKey: T['key']
  onHover?: (key: T['key'] | null) => void
}

interface MultipleProps<T extends { key: string; label: React.ReactNode }> extends BaseProps<T> {
  type: 'multiple'
  selectedKey: T['key'][]
}

type Props<T extends { key: string; label: React.ReactNode }> = SingleProps<T> | MultipleProps<T>

export function ToggleMenu<T extends { key: string; label: React.ReactNode }>(props: Props<T>) {
  const { options, onClick, flexDirection } = props
  const commonProps = {
    className: `flex ${flexDirection === 'column' ? 'flex-col' : 'flex-row gap-1'} w-full h-full`,
  }

  if (props.type === 'multiple') {
    return (
      <ToggleGroup type="multiple" value={props.selectedKey} {...commonProps}>
        {options.map(({ key, label }) => (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <ToggleGroupItem
                key={key}
                value={key}
                aria-label={typeof label === 'string' ? label : undefined}
                onClick={() => onClick(key)}
                className='hover:text-black[&_svg]:size-4 hover:bg-hover w-full font-normal transition-colors data-[state="on"]:bg-gray-200 data-[state="on"]:text-black'>
                {label}
              </ToggleGroupItem>
            </TooltipTrigger>
            <TooltipContent className="w-full">
              <p>{key.charAt(0).toUpperCase() + key.slice(1)}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </ToggleGroup>
    )
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      <ToggleGroup type="single" value={props.selectedKey} {...commonProps}>
        {options.map(({ key, label }) => (
          <ToggleGroupItem
            key={key}
            value={key}
            aria-label={typeof label === 'string' ? label : undefined}
            onClick={() => onClick(key)}
            onMouseEnter={() => props.type === 'single' && props.onHover?.(key)}
            onMouseLeave={() => props.type === 'single' && props.onHover?.(null)}
            className='hover:bg-hover px-2 text-sm font-normal transition-colors hover:text-black data-[state="on"]:bg-gray-200 data-[state="on"]:text-black [&_svg]:size-4'>
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}
